import { useEffect, useState } from 'react';
import { ART, COIN, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { WEB_URL } from '../lib/platform.js';
import { useNav } from '../lib/router.js';
import { referralWhat, useStore } from '../lib/store.js';
import { Scroll, TopBar } from '../components/ui.js';
import type { OrderDto } from '@tikkas/shared';

export function Invite() {
  const s = useStore();
  const { navigate } = useNav();
  const u = s.me?.customer;
  const link = u ? `${WEB_URL}/join?ref=${u.referralCode}` : '';
  const reward = s.config?.referral ?? null;
  const what = referralWhat(s); // "100 points" or "free Tikki Tots", from the referral offer in the admin
  const What = what.replace(/^./, (x) => x.toUpperCase());
  const copy = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${What} at Tikkas`, text: `Use my code ${u?.referralCode} on your first Tikkas order and we both get ${what}.`, url: link });
      else {
        await navigator.clipboard.writeText(link);
        s.toast('Link copied');
      }
    } catch {
      s.toast(link);
    }
  };
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Invite a friend" />
      <div className="card col" style={{ padding: 18, gap: 12, alignItems: 'center', textAlign: 'center' }}>
        <span style={{ width: 64, height: 64, borderRadius: 14, background: 'var(--tile-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Svg html={reward?.kind === 'free_item' ? ART.tots! : COIN(44)} style={{ width: 44, height: 44 }} /></span>
        <h2 className="display" style={{ fontSize: 30, lineHeight: 1.08 }}>{What} for you both</h2>
        <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Your friend enters your code when they join and gets {what} on their first order. You get {what} when they collect it.</p>
      </div>
      {u ? <div className="codecard"><span className="grow col" style={{ gap: 2 }}><span className="small muted">Your code</span><span className="code">{u.referralCode}</span><span className="small muted">{link.replace(/^https?:\/\//, '')}</span></span><button type="button" className="copy" onClick={copy}>Share</button></div> : <button type="button" className="yellow" onClick={() => navigate('/join?next=/invite')}>Sign in to get your code</button>}
      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>Each account needs its own mobile number. {reward?.kind === 'free_item' ? 'Your free item lands in Rewards' : 'Your points land on your card'} when your friend collects their first order.</p>
    </Scroll>
  );
}

export function Soon({ what, blurb }: { what: string; blurb: string }) {
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title={what} />
      <div className="card col" style={{ padding: 18, gap: 10 }}><b style={{ fontSize: 16 }}>Not open yet</b><p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{blurb}</p></div>
    </Scroll>
  );
}

/** Back from a 3-D Secure or wallet redirect: confirm with the server, then show the order. */
export function PayReturn() {
  const s = useStore();
  const { route, navigate } = useNav();
  const [msg, setMsg] = useState('Checking your payment…');
  useEffect(() => {
    const kind = route.search.get('kind') ?? 'order', id = route.search.get('id'), t = route.search.get('t');
    if (!id) {
      navigate('/', { replace: true, root: true });
      return;
    }
    // A guest order (docs/30) confirms by its link token and lands on its own status page.
    api<{ status: string; message?: string; order?: OrderDto }>(t && kind === 'order' ? `/api/guest/orders/${id}/confirm?t=${t}` : `${kind === 'order' ? '/api/orders' : '/api/credit/topups'}/${id}/confirm`, { body: {} })
      .then(async (r) => {
        if (t && kind === 'order') {
          if (r.status === 'succeeded' && r.order) {
            s.addGuestOrder({ id, token: t, number: r.order.number, readyAt: r.order.readyAt, name: r.order.guest?.name ?? '', phone: '', at: new Date().toISOString() });
            s.clearBag();
            s.setFx({ kind: 'placed', order: r.order });
            navigate(`/order/${id}?t=${t}`, { replace: true, root: true });
          } else if (r.status === 'cancelled') setMsg(r.message ?? 'This order was cancelled.');
          else setMsg(r.message ?? 'The payment did not go through. Your bag is still here.');
          return;
        }
        await s.refreshMe();
        if (r.status === 'succeeded') {
          if (kind === 'order' && r.order?.channel === 'catering') {
            // A catering order (or an extra-cost change on one): back to its own page.
            navigate(r.order.parentOrderId ? `/catering/order/${r.order.parentOrderId}` : `/catering/order/${id}?booked=1`, { replace: true, root: true });
          } else if (kind === 'order') {
            s.clearBag();
            if (r.order) s.setFx({ kind: 'placed', order: r.order });
            navigate(`/order/${id}`, { replace: true, root: true });
          } else {
            s.toast('Credit added');
            navigate('/card', { replace: true, root: true });
          }
        } else if (r.status === 'cancelled') setMsg(r.message ?? 'This order was cancelled.');
        else setMsg(r.message ?? 'The payment did not go through. Your bag is still here.');
      })
      .catch((e: Error) => setMsg(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <Scroll><p className="muted">{msg}</p><button type="button" className="yellow" onClick={() => navigate('/bag', { replace: true })}>Back to your bag</button></Scroll>;
}
